// meeting-by-purpose-chart.tsx — Meeting tickets by purpose donut chart.
import { PieChart, CHART_COLORS } from '@vnetwork/ui/charts';
import { Card, CardBody, CardTitle, Skeleton } from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { PieChart as PieIcon } from 'lucide-react';
import type { MeetingTicketsByPurpose } from '@/api/tickets-statistics';

interface Props {
  data: MeetingTicketsByPurpose[] | undefined;
  isLoading: boolean;
  isError: boolean;
}

export function MeetingByPurposeChart({ data, isLoading, isError }: Props) {
  const slices = (data ?? [])
    .filter((d) => d.total_tickets > 0)
    .map((d, i) => ({
      name: d.ticket_meeting_purpose.replace(/_/g, ' '),
      value: d.total_tickets,
      color: CHART_COLORS[i % CHART_COLORS.length],
    }));

  const total = slices.reduce((s, x) => s + x.value, 0);

  return (
    <Card flat>
      <CardBody>
        <CardTitle style={{ marginBottom: 'var(--vn-space-3)' }}>Meeting Tickets by Purpose</CardTitle>
        {isError ? (
          <Alert tone="danger" title="Failed to load meeting data" />
        ) : isLoading ? (
          <Skeleton style={{ height: 220 }} />
        ) : slices.length === 0 ? (
          <EmptyState
            icon={<PieIcon size={20} />}
            title="No meeting tickets"
            description="No meeting tickets found in this date range."
          />
        ) : (
          <PieChart
            donut
            height={220}
            data={slices}
            showLegend
            valueFormatter={(v) => `${v ?? 0} tickets`}
            centerLabel={
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 'var(--vn-font-size-xl)', fontWeight: 'var(--vn-font-weight-semibold)', color: 'var(--vn-fg-default)' }}>
                  {total.toLocaleString('en-US')}
                </div>
                <div style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>meetings</div>
              </div>
            }
          />
        )}
      </CardBody>
    </Card>
  );
}
